import React, { useState } from 'react';
import { 
  Mail, 
  Copy, 
  Check, 
  ExternalLink,
  MessageSquare,
  Globe
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";

const ContactSupport = ({ children, className }) => {
  const [copied, setCopied] = useState(false);
  const email = "support@ovalu.app";
  
  // Gmail Web Compose Link
  const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}`;
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    toast.success("Email address copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children || (
          <Button variant="outline" className={className}>
            Contact Support
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-white/95 backdrop-blur-xl border-pink-100 rounded-[2rem]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-[#FF6B9D]" />
            Contact Support
          </DialogTitle>
          <DialogDescription className="text-gray-600 text-lg">
            Choose how you'd like to reach us. We usually respond within 24 hours.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          {/* Gmail Option - Specifically requested by user */}
          <Button 
            variant="outline" 
            className="w-full justify-between h-14 text-lg border-pink-100 hover:border-[#FF6B9D] hover:bg-pink-50/50 group transition-all rounded-2xl"
            onClick={() => window.open(gmailLink, '_blank')}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-red-600" />
              </div>
              <span className="font-semibold text-gray-900">Open in Gmail</span>
            </div>
            <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#FF6B9D]" />
          </Button>

          {/* Standard Mail App Option */}
          <Button 
            variant="outline" 
            className="w-full justify-between h-14 text-lg border-pink-100 hover:border-[#FF6B9D] hover:bg-pink-50/50 group transition-all rounded-2xl"
            onClick={() => window.location.href = `mailto:${email}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <span className="font-semibold text-gray-900">Open Mail App</span>
            </div>
            <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#FF6B9D]" />
          </Button>

          {/* Copy Email Option */}
          <Button 
            variant="ghost" 
            className="w-full justify-between h-14 text-lg hover:bg-gray-100 group transition-all rounded-2xl"
            onClick={copyToClipboard}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-gray-600" />}
              </div>
              <div className="flex flex-col items-start">
                <span className="font-semibold text-gray-900">{email}</span>
                <span className="text-xs text-gray-500">Copy to clipboard</span>
              </div>
            </div>
            {copied ? (
              <span className="text-xs font-bold text-green-600">Copied!</span>
            ) : (
              <Copy className="w-4 h-4 text-gray-400 group-hover:text-gray-900" />
            )}
          </Button>
        </div>

        <div className="text-center text-xs text-gray-400 mt-2">
          Available 24/7 • Response time &lt; 24h
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactSupport;
